import { useState } from 'react';
import { useIntegrations } from './useIntegrations';
import {
  STRIPE_OAUTH_CONFIG,
  createStripeIntegration,
  fetchStripeAccount,
  revokeStripeIntegration,
  type StripeTokenResponse,
} from '@/lib/integrations/stripe';
import type { StripeIntegration } from '@/contexts/IntegrationsContext';

export interface StripeConnectConfig {
  /** Your Stripe Connect application client ID */
  clientId: string;
  /** Redirect URI registered with Stripe */
  redirectUri: string;
}

export interface StripeConnectState {
  isConnecting: boolean;
  error: string | null;
}

/**
 * Hook for managing Stripe Connect OAuth integration
 */
export function useStripeConnect(config: StripeConnectConfig) {
  const [state, setState] = useState<StripeConnectState>({
    isConnecting: false,
    error: null,
  });
  const { addIntegration, removeIntegration, getIntegrationsByType } = useIntegrations();

  // Check if Stripe Connect is configured
  const isConfigured = !!(config.clientId && config.redirectUri);

  // Get all Stripe integrations
  const stripeIntegrations = getIntegrationsByType<StripeIntegration>('stripe');

  /**
   * Initiates the Stripe Connect OAuth flow
   */
  const initiateConnect = () => {
    if (!config.clientId) {
      setState((prev) => ({
        ...prev,
        error: 'Stripe Connect client ID not configured',
      }));
      return;
    }

    try {
      // Generate a random state parameter for security
      const state = crypto.randomUUID();

      // Store state for later verification
      localStorage.setItem('stripe_oauth_state', state);

      // Build authorization URL
      const params = new URLSearchParams({
        client_id: config.clientId,
        state: state,
        scope: STRIPE_OAUTH_CONFIG.scope,
        redirect_uri: config.redirectUri,
        response_type: 'code',
      });

      // Redirect to Stripe Connect authorization page
      window.location.href = `${STRIPE_OAUTH_CONFIG.authorizeUrl}?${params.toString()}`;
    } catch (error) {
      console.error('Failed to initiate Stripe Connect:', error);
      setState((prev) => ({
        ...prev,
        error: 'Failed to initiate Stripe Connect. Please try again.',
      }));
    }
  };

  /**
   * Handles the OAuth callback after user authorizes
   */
  const handleCallback = async (
    code: string,
    stateParam: string
  ): Promise<StripeIntegration | null> => {
    setState({ isConnecting: true, error: null });

    try {
      // Verify state parameter
      const storedState = localStorage.getItem('stripe_oauth_state');
      if (!storedState || storedState !== stateParam) {
        throw new Error('Invalid OAuth state parameter');
      }

      // Clean up stored state
      localStorage.removeItem('stripe_oauth_state');

      // Exchange authorization code for access token
      const tokenParams = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        client_id: config.clientId,
      });

      const tokenResponse = await fetch(STRIPE_OAUTH_CONFIG.tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: tokenParams.toString(),
      });

      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        throw new Error(
          `Stripe token exchange failed: ${tokenResponse.statusText} - ${errorText}`
        );
      }

      const tokenData: StripeTokenResponse = await tokenResponse.json();

      if (!tokenData.access_token) {
        throw new Error('No access token received from Stripe');
      }

      // Fetch account information
      let accountInfo;
      try {
        accountInfo = await fetchStripeAccount(tokenData.access_token);
      } catch (error) {
        console.warn('Failed to fetch Stripe account info:', error);
        // Continue without account info - it's optional
      }

      // Create integration object
      const integrationId = crypto.randomUUID();
      const integrationName =
        accountInfo?.business_profile?.name ||
        accountInfo?.email ||
        `Stripe ${tokenData.livemode ? 'Live' : 'Test'}`;

      const integration = await createStripeIntegration(
        integrationId,
        integrationName,
        tokenData,
        accountInfo
      );

      // Add to integrations
      addIntegration(integration);

      setState({ isConnecting: false, error: null });
      return integration;
    } catch (error) {
      console.error('Stripe Connect error:', error);

      // Clean up on error
      localStorage.removeItem('stripe_oauth_state');

      setState({
        isConnecting: false,
        error: error instanceof Error ? error.message : 'Stripe Connect failed',
      });
      return null;
    }
  };

  /**
   * Disconnects a Stripe integration
   */
  const disconnect = async (integration: StripeIntegration) => {
    try {
      // Revoke the access on Stripe's side
      await revokeStripeIntegration(config.clientId, integration.stripeAccountId);

      // Remove from local storage
      removeIntegration(integration.id);
    } catch (error) {
      console.error('Failed to disconnect Stripe integration:', error);
      throw error;
    }
  };

  return {
    ...state,
    isConfigured,
    stripeIntegrations,
    initiateConnect,
    handleCallback,
    disconnect,
  };
}
